import type { ScopesObject } from "@/shared/types";
import { toSearchablePath } from "@/shared/functions";
import {
  evidenceCategoryScopeFieldsWithWireTokens,
  excludedContentCategoriesFromScopes,
} from "@/components/evidence-locker-ui-shared-new";

/** Map file-type chip labels to BKB ObjectCategory values for content filtering. */
const fileTypeToCategoryMap: Record<string, string> = {
  PDF: "PDF",
  "MS Office": "OFFICE",
  Image: "IMAGE",
  Video: "VIDEO",
  Audio: "AUDIO",
  Spreadsheet: "SPREADSHEET",
  Email: "EMAIL",
  Other: "OTHER",
};

export type AssistantChatScopeFields = {
  files: string[];
  scopes: string[];
  excludedFiles: string[];
  excludedScopes: string[];
  contentCategories?: string[];
  excludedContentCategories?: string[];
};

export type AssistantChatOptimisticScopesObject = Pick<
  ScopesObject,
  | "scopes"
  | "files"
  | "excludedScopes"
  | "excludedFiles"
  | "evidenceCategoryScopes"
  | "excludedEvidenceCategoryScopes"
  | "evidenceCategoryChips"
  | "evidenceAllFileTypes"
  | "contentCategories"
  | "excludedContentCategories"
>;

export type BuildAssistantChatScopeFieldsResult = {
  bodyFields: AssistantChatScopeFields;
  optimisticScopesObject: AssistantChatOptimisticScopesObject;
};

/**
 * Shape scope state for assistant POST `/chat` and the optimistic turn's
 * `scopesObject`, mirroring `chat.tsx` `onQuerySubmit`.
 */
export function buildAssistantChatScopeFields(
  scopesAndFiles: ScopesObject,
  options: { contentCategoriesEnabled?: boolean } = {},
): BuildAssistantChatScopeFieldsResult {
  const files = scopesAndFiles.files ?? [];
  const scopes = scopesAndFiles.scopes ?? [];
  const excludedFiles = scopesAndFiles.excludedFiles ?? [];
  const excludedScopes = scopesAndFiles.excludedScopes ?? [];

  const selectedContentCategories =
    (scopesAndFiles.evidenceCategoryScopes?.length ?? 0) > 0
      ? scopesAndFiles.evidenceCategoryScopes!
      : scopesAndFiles.contentCategories ?? [];

  const contentCategories = [
    ...new Set(
      selectedContentCategories
        .map((fileType) => fileTypeToCategoryMap[fileType])
        .filter((c): c is string => typeof c === "string" && c.length > 0)
        .map((c) => c.toLowerCase()),
    ),
  ];

  const excludedContentCategories = [
    ...new Set(
      excludedContentCategoriesFromScopes(
        scopesAndFiles.excludedEvidenceCategoryScopes,
      )
        .filter((c): c is string => typeof c === "string" && c.length > 0)
        .map((c) => c.toLowerCase()),
    ),
  ];

  const bodyFields: AssistantChatScopeFields = {
    files: files.map(toSearchablePath),
    scopes: scopes.map(toSearchablePath),
    excludedFiles: excludedFiles.map(toSearchablePath),
    excludedScopes: excludedScopes.map(toSearchablePath),
    ...(options.contentCategoriesEnabled &&
      contentCategories.length > 0 && { contentCategories }),
    ...(options.contentCategoriesEnabled &&
      excludedContentCategories.length > 0 && { excludedContentCategories }),
  };

  const optimisticIncludedScopes =
    (scopesAndFiles.evidenceCategoryScopes?.length ?? 0) > 0
      ? [...(scopesAndFiles.evidenceCategoryScopes ?? [])]
      : [...(scopesAndFiles.contentCategories ?? [])];
  const optimisticExcludedScopes = [
    ...(scopesAndFiles.excludedEvidenceCategoryScopes ?? []),
  ];
  const optimisticCategoryFields = evidenceCategoryScopeFieldsWithWireTokens(
    optimisticIncludedScopes,
    optimisticExcludedScopes,
    !!scopesAndFiles.evidenceAllFileTypes,
  );

  return {
    bodyFields,
    optimisticScopesObject: {
      scopes: [...scopes],
      files: [...files],
      excludedScopes: [...excludedScopes],
      excludedFiles: [...excludedFiles],
      ...optimisticCategoryFields,
    },
  };
}
